'use client';

import { css } from '@bts-web/utils-style-engine';
import { PillBar } from '@bts-web/core-features/common';
import { AnchorClusterKey } from './anchorNavigationConfig';
import { useAnchorScrollBehavior } from './useAnchorScrollBehavior';
import { useIsBelowScreenWidth } from '@bts-web/core-features/layouts';

export interface AnchorNavigationAvailability {
  keyFacts: boolean;
  research: boolean;
  activity: boolean;
}

interface AnchorNavigationProps {
  availability: AnchorNavigationAvailability;
  translations: {
    anchorKeyFacts: string;
    anchorResearch: string;
    anchorActivity: string;
  };
}

// literal values kept in-line so PandaCSS extracts them statically; they mirror
// ANCHOR_BAR_TOP in anchorNavigationConfig.ts.
const stickyContainerStyles = css({
  position: 'sticky',
  top: { base: '52px', lg: '71px' },
  zIndex: 1,
  transition: 'top 0.2s ease',
  backgroundColor: 'screen_background.primary',
  paddingTop: 'extra_small',
  paddingBottom: 'extra_small',
  paddingInlineStart: { base: 'small', md: 'medium' },
  paddingInlineEnd: { base: 'small', md: 'medium' },
  // header slid away on scroll-down -> bar owns the very top (mobile only)
  '&[data-header-hidden="true"]': {
    top: { base: '0px' },
  },
});

export const AnchorNavigation = ({
  availability,
  translations,
}: AnchorNavigationProps) => {
  const { activeCluster, headerHidden, scrollToCluster } =
    useAnchorScrollBehavior();

  const isDesktop = !useIsBelowScreenWidth(1024);

  if (isDesktop) {
    return null;
  }

  const clusters: {
    value: AnchorClusterKey;
    label: string;
    available: boolean;
  }[] = [
    {
      value: 'key_facts',
      label: translations.anchorKeyFacts,
      available: availability.keyFacts,
    },

    {
      value: 'research',
      label: translations.anchorResearch,
      available: availability.research,
    },
    {
      value: 'activity',
      label: translations.anchorActivity,
      available: availability.activity,
    },
  ];

  const items = clusters
    .filter((cluster) => cluster.available)
    .map(({ value, label }) => ({ value, label }));

  // nothing to jump between -> no bar
  if (items.length <= 1) {
    return null;
  }

  const selectedValue =
    activeCluster && items.some((item) => item.value === activeCluster)
      ? activeCluster
      : items[0].value;

  return (
    <div className={stickyContainerStyles} data-header-hidden={headerHidden}>
      <PillBar
        size="small"
        items={items}
        value={selectedValue}
        onValueChange={(value) => scrollToCluster(value as AnchorClusterKey)}
      />
    </div>
  );
};
